import styles from "./HamburgerMenu.module.css";
import { IconButton } from "src/components/ui-components/Button/IconButton";
import { AnchorButton } from "src/components/ui-components/Button/AnchorButton";
import { Button } from "src/components/ui-components/Button/Button";
import GlobalSearch from "src/components/GlobalSearch/GlobalSearch";
import * as Menu from "src/components/ui-components/MenuPanel/MenuPanel";
import { CreateDashboardDialog } from "src/components/page-components/DashboardPage/create";

import { Identity } from "src/components/ui-components/Identity";
import { IconList } from "src/components/ui-components/Icons/IconList";
import { IconCross } from "src/components/ui-components/Icons/IconCross";

import { useContext, useState } from "react";
import { SessionContext } from "src/components/session/session";
import { useActiveContext } from "shared/ContextSwitcher/store";
import { useLoginUrl, getLoginUrlWithNextUrl } from "src/hooks/useLoginUrl";
import { useSignupUrl } from "src/hooks/useSignupUrl";
import { ModalWithoutTrigger } from "src/components/ui-components/Modal";
import { DataUploadModalContent } from "../../DataUpload/DataUploadModalContent";
import { IconSun } from "../../../src/components/ui-components/Icons/IconSun";
import { IconMoonStars } from "../../../src/components/ui-components/Icons/IconMoonStars";
import { useDarkMode } from "../../../src/hooks/useDarkMode";

export function HamburgerMenu() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isDataUploadModalOpen, setIsDataUploadModalOpen] = useState(false);

  const [isCreateDashboardDialogOpen, setIsCreateDashboardDialogOpen] =
    useState(false);

  const { session, sessionLoading } = useContext(SessionContext);
  const isLoggedOut = !session && !sessionLoading;
  const loginUrl = useLoginUrl();
  const signupUrl = useSignupUrl();
  const activeContext = useActiveContext();
  const { mode, changeMode } = useDarkMode();

  return (
    <>
      <IconButton
        theme="tertiary"
        size="M"
        onClick={() => setIsMenuOpen((open) => !open)}
      >
        {!isMenuOpen ? <IconList /> : <IconCross />}
      </IconButton>

      {isMenuOpen && (
        <nav className={styles.menu}>
          <Button
            size="S"
            theme="ghost"
            className={styles.modeButton}
            onClick={() => {
              changeMode(mode === "light" ? "dark" : "light");
            }}
          >
            {mode === "light" ? <IconSun /> : <IconMoonStars />}
            {`Change to ${mode === "light" ? "dark" : "light"} mode`}
          </Button>
          <GlobalSearch />

          <ul>
            <Menu.ItemLink href="/browse/dashboards">Discover</Menu.ItemLink>
            <Menu.ItemLink href="/browse/dashboards/favorite">
              Favorites
            </Menu.ItemLink>
          </ul>

          {activeContext && (
            <>
              <hr />

              <nav className={styles.context}>
                <h2>
                  <Identity
                    size="L"
                    color="inherit"
                    inline={false}
                    owner={activeContext}
                  />
                </h2>

                <ul>
                  <Menu.ItemLink href="/workspace/library">
                    Library
                  </Menu.ItemLink>

                  {activeContext?.permissions.canEditContent && (
                    <li>
                      <ul className={styles.buttonGroup}>
                        <li>
                          <AnchorButton
                            theme="primary-light"
                            size="M"
                            onClick={() => {
                              setIsMenuOpen(false);
                            }}
                            href={
                              session ? "/ai" : getLoginUrlWithNextUrl("/ai")
                            }
                          >
                            <span>Dune AI</span>
                          </AnchorButton>
                        </li>
                        <li>
                          <AnchorButton
                            theme="primary-light"
                            size="M"
                            href={
                              session
                                ? "/queries"
                                : getLoginUrlWithNextUrl("/queries")
                            }
                            onClick={() => {
                              setIsMenuOpen(false);
                            }}
                          >
                            <span>New query</span>
                          </AnchorButton>
                        </li>

                        <li>
                          <Button
                            theme="primary-light"
                            size="M"
                            onClick={() => {
                              setIsMenuOpen(false);
                              setIsCreateDashboardDialogOpen(true);
                            }}
                          >
                            <span>New dashboard</span>
                          </Button>
                        </li>
                      </ul>
                    </li>
                  )}
                </ul>
              </nav>
            </>
          )}

          {isLoggedOut && (
            <>
              <hr />

              <nav>
                <ul>
                  <li>
                    <ul className={styles.buttonGroup}>
                      <li>
                        <AnchorButton
                          theme="primary-light"
                          size="M"
                          href={getLoginUrlWithNextUrl("/queries")}
                          onClick={() => {
                            setIsMenuOpen(false);
                          }}
                        >
                          <span>New query</span>
                        </AnchorButton>
                      </li>

                      <li>
                        <AnchorButton
                          theme="primary-light"
                          size="M"
                          href={loginUrl}
                        >
                          <span>New dashboard</span>
                        </AnchorButton>
                      </li>
                    </ul>
                  </li>

                  <li>
                    <ul className={styles.buttonGroup}>
                      <li>
                        <AnchorButton
                          theme="secondary"
                          size="M"
                          href={signupUrl}
                          onClick={() => setIsMenuOpen(false)}
                        >
                          <span>Sign up</span>
                        </AnchorButton>
                      </li>

                      <li>
                        <AnchorButton
                          theme="secondary-light"
                          size="M"
                          href={loginUrl}
                          onClick={() => setIsMenuOpen(false)}
                        >
                          <span>Sign in</span>
                        </AnchorButton>
                      </li>
                    </ul>
                  </li>
                </ul>
              </nav>
            </>
          )}

          <hr />

          <ul>
            <Menu.ItemLink href="https://dunecon.com">
              Join us at Dunecon
            </Menu.ItemLink>
            <Menu.ItemLink href="/docs" target="_blank">
              Docs
            </Menu.ItemLink>
            <Menu.ItemLink href="https://discord.gg/ErrzwBz" target="_blank">
              Discord
            </Menu.ItemLink>
            <Menu.ItemLink href="/feedback">Give feedback</Menu.ItemLink>
            <Menu.ItemLink href="/projects">Projects</Menu.ItemLink>
            <Menu.ItemLink href="/blog">Blog</Menu.ItemLink>
            <Menu.ItemLink href="/pricing">Pricing</Menu.ItemLink>
            <Menu.ItemLink
              href="https://dune.com/docs/resources/dune-bounties/"
              target="_blank"
            >
              Submit a bounty
            </Menu.ItemLink>
            <Menu.ItemLink href="/careers">Careers</Menu.ItemLink>
            <Menu.ItemLink href="https://status.dune.com/" target="_blank">
              Status
            </Menu.ItemLink>
            <Menu.ItemLink href="/contracts/new">
              Submit a contract
            </Menu.ItemLink>
            <Menu.ItemButton
              onClick={() => {
                setIsDataUploadModalOpen(true);
              }}
            >
              Upload a dataset
            </Menu.ItemButton>
          </ul>
        </nav>
      )}

      {session && (
        <CreateDashboardDialog
          isOpen={isCreateDashboardDialogOpen}
          close={() => setIsCreateDashboardDialogOpen(false)}
          redirect={true}
        />
      )}
      {session && (
        <ModalWithoutTrigger
          label="Upload a dataset"
          size="M"
          content={
            <DataUploadModalContent
              onDismiss={() => setIsDataUploadModalOpen(false)}
            />
          }
          isOpen={isDataUploadModalOpen}
          onDismiss={undefined}
        />
      )}
    </>
  );
}
